import { useGlobal, useTranslate } from '@ur-solutions/react-hooks'
import type { Overrides } from '.'
import { useCallback, useEffect, useRef } from 'react'

export function useBreadcrumbs() {
  const [overrides, setOverrides] = useGlobal('breadcrumbs.overrides')
  const [inserts, setInserts] = useGlobal('breadcrumbs.inserts')
  const [excludes, setExcludes] = useGlobal('breadcrumbs.excludes')

  /**
   * Writing an unchanged value would still publish a new overrides object to
   * every subscriber, so a caller that re-runs on each render — a breadcrumb
   * effect waiting for its query, say — would re-render itself forever.
   */
  const setOverride = useCallback(
    (key: string, value: string | null) => {
      if (overrides[key] === value) return

      setOverrides({
        ...overrides,
        [key]: value,
      })
    },
    [overrides, setOverrides]
  )

  const deleteOverrides = useCallback(
    (...keys: string[]) => {
      const includeKeys = Object.keys(overrides).filter(
        key => !keys.includes(key)
      )
      const filteredOverrides = includeKeys.reduce<Overrides>((acc, key) => {
        return {
          ...acc,
          [key]: overrides[key] ?? null,
        }
      }, {})
      setOverrides(filteredOverrides)
    },
    [overrides, setOverrides]
  )

  return {
    overrides,
    setOverride,
    setOverrides,
    deleteOverrides,

    inserts,
    setInserts,

    excludes,
    setExcludes,
  }
}

export function useBreadcrumbOverride(
  key: string | null,
  value: string | null,
  withLoading = true
) {
  const translations = useTranslate({
    loading: 'common.loading-ellipsis',
  })

  const { overrides, setOverride } = useBreadcrumbs()
  const overridesRef = useRef(overrides)

  useEffect(() => {
    if (key === null) return

    const nextValue =
      withLoading && value === null ? translations.loading : value
    if (overridesRef.current[key] === nextValue) return

    setOverride(key, nextValue)
    overridesRef.current = { ...overridesRef.current, [key]: nextValue }
  }, [key, value, setOverride, withLoading, translations.loading])
}

export function useBreadcrumbOverrides<
  TOverrides extends Overrides,
  TKey extends keyof TOverrides,
>(overrides: TOverrides, withLoading: boolean | TKey[]) {
  const translations = useTranslate({
    loading: 'common.loading-ellipsis',
  })

  const { overrides: currentOverrides, setOverrides } = useBreadcrumbs()
  const overridesRef = useRef(currentOverrides)

  useEffect(() => {
    for (const [key, value] of Object.entries(overrides)) {
      const shouldUseLoading =
        withLoading === true ||
        (Array.isArray(withLoading) && withLoading.includes(key as TKey))
      const nextValue =
        shouldUseLoading && value === null ? translations.loading : value
      if (overridesRef.current[key] === nextValue) continue

      overridesRef.current = { ...overridesRef.current, [key]: nextValue }
    }

    setOverrides(overridesRef.current)
  }, [overrides, setOverrides, translations.loading, withLoading])
}
